/**
 * Styled-components for the INSi search modal + its preview panel, split out of
 * InsiSearchModal so the component file stays focused on behaviour.
 */
import styled from 'styled-components';
import {
  Box as MuiBox,
  Typography,
  Button,
  ToggleButtonGroup,
} from '@mui/material';

export const SFields = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

export const SRow = styled(MuiBox)`
  display: flex;
  gap: 24px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

export const SField = styled(MuiBox)`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SAddNameButton = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.colors.blue100};
`;

export const SSexRow = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 322px;
`;

export const SSexToggle = styled(ToggleButtonGroup)`
  && {
    display: flex;
    gap: 10px;
  }
  && .MuiToggleButton-root {
    flex: 1 1 0;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.palette.colors.gray300};
    border-radius: 5px;
  }
  && .Mui-selected {
    border-color: ${({ theme }) => theme.palette.colors.blue100};
    background: ${({ theme }) => theme.palette.colors.blue200};
    color: ${({ theme }) => theme.palette.colors.blue100};
  }
`;

export const SLabel = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.palette.colors.black100};
  }
`;

const BLUE_DISABLED = '#BDD3FF';

export const SSearchButton = styled(Button)`
  && {
    height: 48px;
    border-radius: 8px;
    text-transform: none;
    font-weight: 600;
    font-size: 16px;
  }
  &&.Mui-disabled {
    background: ${BLUE_DISABLED};
    color: #ffffff;
  }
`;

export const SDivider = styled.div`
  border-top: 1px dashed ${({ theme }) => theme.palette.colors.gray200};
`;

export const SPreviewTitle = styled(Typography)`
  && {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const SPreviewPanel = styled(MuiBox)`
  min-height: 320px;
  background: #f7f7f7;
  border: 1px solid ${({ theme }) => theme.palette.colors.gray200};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

export const SCentered = styled(MuiBox)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SMessagePanel = styled(MuiBox)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
`;

export const SIconBox = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.colors.gray200};
  color: ${({ theme }) => theme.palette.colors.gray400};
`;

export const SMessageTitle = styled(Typography)`
  && {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const SMessageDescription = styled(Typography)`
  && {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.palette.colors.gray400};
  }
`;

export const SPanelActions = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

export const SFoundCard = styled(MuiBox)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.palette.colors.bgGreen};
  border: 1px solid ${({ theme }) => theme.palette.colors.green100};
  border-radius: 8px;
  padding: 12px;
`;

export const SFoundHeader = styled(MuiBox)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.colors.green400};
`;

export const SDetailRow = styled(MuiBox)`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  gap: 12px;

  .label {
    color: ${({ theme }) => theme.palette.colors.black100};
    opacity: 0.7;
  }
  .value {
    font-weight: 600;
    text-align: right;
  }
`;

export const SInsNumber = styled(MuiBox)`
  margin-top: 8px;
  padding: 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.palette.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.colors.green100};
  display: flex;
  flex-direction: column;

  .label {
    font-size: 12px;
    opacity: 0.7;
  }
  .value {
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 1px;
    color: ${({ theme }) => theme.palette.colors.green400};
  }
`;

/* One shared grid for every row → type / matricule / period line up in columns
   even when a period is missing or a different length. */
export const SHistoryGrid = styled(MuiBox)`
  width: 100%;
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  column-gap: 16px;
  row-gap: 6px;
  align-items: center;
  font-size: 13px;

  .type {
    opacity: 0.7;
  }
  .value {
    font-weight: 600;
    letter-spacing: 1px;
  }
  .period {
    opacity: 0.7;
    white-space: nowrap;
    text-align: right;
  }
`;

export const SDedup = styled(MuiBox)`
  margin-top: 8px;
  padding: 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.palette.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.colors.gray200};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SFooter = styled(MuiBox)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 30px 30px;
  border-top: 1px solid ${({ theme }) => theme.palette.colors.gray200};
`;

export const SCancelButton = styled(Button)`
  && {
    background: #f7f7f7;
    color: ${({ theme }) => theme.palette.text.primary};
    border-radius: 6px;
    text-transform: none;
    font-weight: 600;

    :hover {
      background: ${({ theme }) => theme.palette.colors.gray200};
    }
  }
`;

export const SConfirmButton = styled(Button)`
  && {
    border-radius: 6px;
    text-transform: none;
    font-weight: 600;
  }
  &&.Mui-disabled {
    background: ${BLUE_DISABLED};
    color: #ffffff;
  }
`;
